import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getSettings } from '@/api/apiClient/settings';
import { getSetupStatus } from '@/api/apiClient/setup';
import type { RepoInfo } from '@/api/types';

/**
 * Server configuration state (the v2 analogue of the reference app's config
 * store). v2 has no `/api/config`; "config" is the settings surface.
 */
export const useConfigStore = defineStore('config', () => {
  const configured = ref(false);
  const mode = ref<'local' | 'github'>('github');
  const cloneUrl = ref('');
  const owner = ref<string | undefined>(undefined);
  const repo = ref<string | undefined>(undefined);
  const repoInfo = ref<RepoInfo | undefined>(undefined);
  const loaded = ref(false);
  // Whether a GitHub PAT is saved on the server. Defaults to true — "assume the
  // registrar is configured until told otherwise" — so an unreachable
  // `/api/setup` signal never wrongly prompts for a token that in fact exists.
  // It is derived from the public `/api/setup` endpoint, not the admin-only
  // `/api/settings`, so a signed-in non-admin also learns setup is pending.
  const hasToken = ref(true);

  // Without a PAT the registrar can never read the repository, so every browse
  // surface fails to load. This is the signal those surfaces read to show a
  // "set a token in Settings" notice instead of a generic "could not load".
  const needsSetup = computed(() => !hasToken.value);

  async function fetchConfig(): Promise<void> {
    // The public setup signal — readable by every visitor — is the source of
    // truth for whether a token exists; a signed-in non-admin gets it even
    // though the admin-only `/api/settings` below 403s them.
    try {
      const status = await getSetupStatus();
      hasToken.value = !status.needsSetup;
      mode.value = status.mode ?? 'github';
      cloneUrl.value = status.cloneUrl ?? '';
    } catch {
      cloneUrl.value = '';
      // Leave the default; an unreachable signal must not wrongly prompt setup.
    }
    // Admin-only repo details (owner/repo/repoInfo); a non-admin 403s here and
    // keeps defaults — the setup signal above already answered "needs a token?".
    try {
      const s = await getSettings();
      configured.value = s.configured;
      owner.value = s.owner;
      repo.value = s.repo;
      repoInfo.value = s.repoInfo;
    } catch {
      // Leave defaults; the shell still renders even if settings are unreachable.
    } finally {
      loaded.value = true;
    }
  }

  return {
    mode,
    cloneUrl,
    configured,
    owner,
    repo,
    repoInfo,
    loaded,
    hasToken,
    needsSetup,
    fetchConfig,
  };
});
